import { CommonModule } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    ElementRef,
    inject,
    input,
    viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { ArrayPipe, ImageComponent } from '@interacta-shared/ui';
import { LoadingLayerService } from '@interacta-shared/util-common';
import { TranslatePipe } from '@ngx-translate/core';
import { of, timer } from 'rxjs';
import { map } from 'rxjs/operators';

@Component({
    imports: [CommonModule, ImageComponent, TranslatePipe, ArrayPipe],
    selector: 'interacta-loading-layer',
    templateUrl: './loading-layer.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LoadingLayerComponent {
    private readonly loadingLayerService = inject(LoadingLayerService);
    private readonly layerEl =
        viewChild.required<ElementRef<HTMLElement>>('layerEl');
    readonly src = input.required<string>();

    readonly state = this.loadingLayerService.state;

    readonly points$ = timer(0, 1000).pipe(map((t) => t % 4));

    constructor() {
        toObservable(this.loadingLayerService.state)
            .pipe(takeUntilDestroyed())
            .subscribe(({ loading }) => {
                const el = this.layerEl().nativeElement;
                if (loading) {
                    try {
                        el.showPopover();
                    } catch {
                        // Ignore DOMException if already open or not supported
                    }
                } else {
                    try {
                        el.hidePopover();
                    } catch {
                        // Ignore DOMException if already closed or not supported
                    }
                }
            });
    }

    private readonly text = computed(() => this.state().content.text);

    readonly text$ = computed(() => {
        const text = this.text();
        if (typeof text === 'string') {
            return of(text);
        } else {
            return timer(0, 5000).pipe(map((t) => text[t % text.length]));
        }
    });

    readonly secondaryText$ = timer(6000).pipe(
        map(() => this.state().content.secondaryText),
    );

    readonly imageSrc = computed(
        () => this.state().content.imageSrc || this.src(),
    );
}
